import { useEffect, useMemo, useRef, useState } from "react";
import { getTourInfo } from "../modules/organizedTours/api/tourInfoClient";
import {
  logOrgTourInfoProfile,
  resolveOrganizedTourInfoRequest,
} from "../utils/resolveOrganizedTourInfoRequest";
import { orgTourInfoResponseHasRichDescription } from "../utils/orgTourRichDescriptionGate";
import { orgTourPerfNow } from "../utils/orgTourPerf";

const INITIAL = {
  loading: false,
  error: null,
  missingFields: [],
  data: null,
  detailFetchAttempted: false,
  richDescriptionAvailable: false,
};

/**
 * Loads LDS getOrgTourInfo (+ resource text) on every profile mount / deal change.
 */
export function useOrganizedTourProfileInfo(selectedResult, routeDealId, options = {}) {
  const savedOrder = options.savedOrder ?? null;
  const request = useMemo(
    () => resolveOrganizedTourInfoRequest(selectedResult, routeDealId, savedOrder),
    [selectedResult, routeDealId, savedOrder]
  );

  const [state, setState] = useState(INITIAL);
  const requestGenRef = useRef(0);
  const selectedSnapshotRef = useRef(selectedResult);
  selectedSnapshotRef.current = selectedResult;

  useEffect(() => {
    const gen = ++requestGenRef.current;
    const {
      dealId,
      tourName,
      supplierId,
      depDate,
      retDate,
      canRequest,
      missingFields,
    } = request;

    if (!dealId) {
      setState({
        loading: false,
        error: null,
        missingFields: ["dealId"],
        data: null,
        detailFetchAttempted: false,
        richDescriptionAvailable: false,
      });
      return;
    }

    if (!canRequest) {
      if (dealId && !selectedSnapshotRef.current && !savedOrder) {
        setState({
          loading: true,
          error: null,
          missingFields: [],
          data: null,
          detailFetchAttempted: false,
          richDescriptionAvailable: false,
        });
        return;
      }
      logOrgTourInfoProfile("SKIP", {
        dealId,
        missingFields,
        reason: "tour_structure_background_requires_deal_supplier_tourName",
      });
      setState({
        loading: false,
        error: null,
        missingFields,
        data: null,
        detailFetchAttempted: false,
        richDescriptionAvailable: false,
      });
      return;
    }

    const payload = { dealId, tourName, supplierId, depDate, retDate };
    logOrgTourInfoProfile("LOAD_START", { dealId });
    logOrgTourInfoProfile("PAYLOAD", payload);
    setState({
      loading: true,
      error: null,
      missingFields: [],
      data: null,
      detailFetchAttempted: true,
      richDescriptionAvailable: false,
    });

    const ac = new AbortController();
    const t0 = orgTourPerfNow();
    getTourInfo(payload, { signal: ac.signal })
      .then((res) => {
        if (gen !== requestGenRef.current) return;
        const ms = orgTourPerfNow() - t0;
        if (import.meta.env?.DEV) {
          console.log("[ORG_TOUR_INFO_FETCH_MS]", { ms: Math.round(ms * 10) / 10, ok: res?.ok });
        }
        logOrgTourInfoProfile("RESPONSE", {
          ok: res?.ok,
          status: res?.status,
          hasText: Boolean(res?.contentText),
          hasSanitizedHtml: Boolean(res?.contentHtmlSanitized),
          hasUrl: Boolean(res?.resourceUrl),
          resourceFetchOk: res?.resourceFetchOk,
        });
        if (!res?.ok) {
          setState({
            loading: false,
            error: res?.error || "org_tour_info_failed",
            missingFields: [],
            data: res,
            detailFetchAttempted: true,
            richDescriptionAvailable: false,
          });
          return;
        }
        const rich = orgTourInfoResponseHasRichDescription(res);
        setState({
          loading: false,
          error: null,
          missingFields: [],
          data: res,
          detailFetchAttempted: true,
          richDescriptionAvailable: rich,
        });
      })
      .catch((err) => {
        if (gen !== requestGenRef.current) return;
        if (err?.code === "ERR_CANCELED" || err?.name === "CanceledError") {
          return;
        }
        logOrgTourInfoProfile("ERROR", {
          dealId,
          message: err?.message || String(err),
        });
        setState({
          loading: false,
          error: "request_failed",
          missingFields: [],
          data: null,
          detailFetchAttempted: true,
          richDescriptionAvailable: false,
        });
      });

    return () => {
      ac.abort();
    };
  }, [
    request.dealId,
    request.tourName,
    request.supplierId,
    request.canRequest,
    request.missingFields.join(","),
    request.depDate,
    request.retDate,
    savedOrder,
  ]);

  return {
    ...state,
    request,
    resourceUrl: state.data?.resourceUrl || null,
    contentText: state.data?.contentText || null,
    contentHtmlSanitized: state.data?.contentHtmlSanitized || null,
    encodingUsed: state.data?.encodingUsed || null,
    parseWarnings: state.data?.parseWarnings || null,
    resourceDescription: state.data?.resourceDescription || null,
    resourceFetchOk: state.data?.resourceFetchOk === true,
  };
}
